import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, User, Activity, HeartPulse, Stethoscope, FileText, CheckCircle2, Clock, TrendingUp, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { Patient, PatientVisitSummary } from '../types';

interface PatientDetailProps {
  patientId: string;
  onBack: () => void;
  onRequestSelect?: (requestId: string) => void;
}

export const PatientDetail: React.FC<PatientDetailProps> = ({ patientId, onBack, onRequestSelect }) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditName, setShowEditName] = useState(false);
  const [editName, setEditName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const patientData = await api.getPatient(patientId);
        setPatient(patientData);
      } catch (err) {
        console.error('Error loading patient', err);
        setError('Unable to load patient details right now.');
      } finally {
        setIsLoading(false);
      }
    };

    if (patientId) {
      fetchPatient();
    }
  }, [patientId]);

  const visitSummaries = useMemo(() => {
    if (!patient?.visitSummaries?.length) return [];
    return [...patient.visitSummaries].sort((a, b) => {
      return new Date(b.visitDate).getTime() - new Date(a.visitDate).getTime();
    });
  }, [patient?.visitSummaries]);

  const formatDate = (iso?: string) => {
    if (!iso) return 'Unknown';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return 'Unknown';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const replacePatientNamePlaceholder = (text: string): string => {
    if (!text || !patient?.name) return text;
    // Replace all variations of patient name placeholders
    return text
      .replace(/\[Patient Name\]/gi, patient.name)
      .replace(/\[Patient's Name\]/gi, patient.name)
      .replace(/\[Patient\]/gi, patient.name);
  };

  const renderWomensHealthProfile = () => {
    if (!patient?.womensHealthProfile) return null;

    const profile = patient.womensHealthProfile;
    const hasAnyData = 
      profile.ageBand || 
      profile.menopausalStatus || 
      profile.gravidaPara || 
      profile.menstrualStatus || 
      profile.sexuallyActive || 
      (profile.contraceptionMethods && profile.contraceptionMethods.length > 0) ||
      profile.cervicalScreeningStatus ||
      profile.lastCervicalScreeningSummary ||
      profile.nextCervicalScreeningDue ||
      profile.mammogramStatus ||
      profile.lastMammogramSummary ||
      profile.nextMammogramDue ||
      profile.colonScreeningStatus ||
      profile.lastColonScreeningSummary ||
      profile.stiScreeningStatus ||
      (profile.vaccinationTags && profile.vaccinationTags.length > 0) ||
      (profile.familyHistoryRiskTags && profile.familyHistoryRiskTags.length > 0) ||
      (profile.chronicGynConditionTags && profile.chronicGynConditionTags.length > 0);

    if (!hasAnyData) return null;

    // Helper to determine if a "due" field indicates upcoming action
    const isUpcoming = (dueText: string | null | undefined): boolean => {
      if (!dueText) return false;
      const lower = dueText.toLowerCase();
      return lower.includes('within') || lower.includes('due') || lower.includes('soon');
    };

    // Helper to determine screening badge style based on clinical urgency
    const getScreeningBadgeStyle = (status: string | null | undefined, nextDue: string | null | undefined, defaultStatus?: string): string => {
      // If no status and no nextDue, check defaultStatus
      const effectiveStatus = status || defaultStatus || '';
      const statusLower = effectiveStatus.toLowerCase();
      const nextDueLower = (nextDue || '').toLowerCase();
      
      // "Due now" or "overdue" - actionable, needs attention
      if (statusLower.includes('due now') || statusLower.includes('overdue') || 
          nextDueLower.includes('due now') || nextDueLower.includes('overdue')) {
        return 'badge-error'; // Red for urgent action needed
      }
      
      // "Due soon" or "within X months" - upcoming but not urgent
      if (statusLower.includes('due soon') || nextDueLower.includes('within') || 
          nextDueLower.includes('due soon')) {
        return 'badge-warning'; // Yellow/orange for upcoming
      }
      
      // "Up to date" or "current" - neutral
      if (statusLower.includes('up to date') || statusLower.includes('current') || 
          statusLower.includes('not yet indicated')) {
        return 'badge-neutral';
      }
      
      // Default to neutral if status is just descriptive
      return 'badge-neutral';
    };

    // Helper to check if a risk factor is a negative statement (should be plain text)
    const isNegativeStatement = (text: string): boolean => {
      const lower = text.toLowerCase();
      return lower.startsWith('no ') || lower.startsWith('negative ') || 
             lower.includes('no family history') || lower.includes('no history of');
    };



    // Format screening summary text only (without status)
    const formatScreeningSummary = (last: string | null | undefined, next: string | null | undefined) => {
      if (last && next) {
        return `${last} → ${next}`;
      } else if (last) {
        return `Last: ${last}`;
      } else if (next) {
        return isUpcoming(next) ? `→ ${next}` : `Next: ${next}`;
      }
      return null;
    };

    return (
      <div className="mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Patient Overview */}
          {(profile.ageBand || profile.menopausalStatus || profile.gravidaPara || profile.menstrualStatus) && (
            <div className="card bg-base-100 border border-base-300 shadow-sm">
              <div className="card-body p-5">
                <h3 className="text-sm font-bold text-base-content/70 uppercase tracking-wide mb-4">Patient Overview</h3>
                <div className="space-y-3">
                  {profile.ageBand && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px]">Age band:</span>
                      <span className="badge badge-neutral badge-sm">{profile.ageBand}</span>
                    </div>
                  )}
                  {profile.menopausalStatus && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px]">Menopause:</span>
                      <span className={`badge badge-sm capitalize ${
                        profile.menopausalStatus.toLowerCase() === 'unknown' 
                          ? 'badge-warning badge-outline' 
                          : 'badge-neutral'
                      }`}>
                        {profile.menopausalStatus}
                      </span>
                    </div>
                  )}
                  {profile.gravidaPara && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px]">G/P:</span>
                      <span className="badge badge-neutral badge-sm">{profile.gravidaPara}</span>
                    </div>
                  )}
                  {profile.menstrualStatus && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px]">Menstrual status:</span>
                      <span className="badge badge-neutral badge-sm">{profile.menstrualStatus}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sexual Health */}
          {(profile.sexuallyActive || (profile.contraceptionMethods && profile.contraceptionMethods.length > 0)) && (
            <div className="card bg-base-100 border border-base-300 shadow-sm">
              <div className="card-body p-5">
                <h3 className="text-sm font-bold text-base-content/70 uppercase tracking-wide mb-4">Sexual Health</h3>
                <div className="space-y-3">
                  {profile.sexuallyActive && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[120px]">Sexually active:</span>
                      <span className="badge badge-info badge-sm capitalize">{profile.sexuallyActive}</span>
                    </div>
                  )}
                  {profile.contraceptionMethods && profile.contraceptionMethods.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-base-content/60 min-w-[120px] pt-1">Contraception:</span>
                      <div className="flex flex-wrap gap-2">
                        {profile.contraceptionMethods.map((method, index) => (
                          <span key={index} className="badge badge-info badge-sm">{method}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Screening Status */}
          {(profile.cervicalScreeningStatus || profile.lastCervicalScreeningSummary || profile.nextCervicalScreeningDue ||
            profile.mammogramStatus || profile.lastMammogramSummary || profile.nextMammogramDue ||
            profile.colonScreeningStatus || profile.lastColonScreeningSummary || profile.stiScreeningStatus ||
            (profile.vaccinationTags && profile.vaccinationTags.length > 0)) && (
            <div className="card bg-base-100 border border-base-300 shadow-sm">
              <div className="card-body p-5">
                <h3 className="text-sm font-bold text-base-content/70 uppercase tracking-wide mb-4">Screening Status</h3>
                <div className="space-y-3">
                  {/* Cervical */}
                  {(profile.cervicalScreeningStatus || profile.lastCervicalScreeningSummary || profile.nextCervicalScreeningDue) && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px] pt-1">Cervical:</span>
                      <div className="flex flex-col gap-1">
                        <span className={`badge badge-sm ${getScreeningBadgeStyle(profile.cervicalScreeningStatus, profile.nextCervicalScreeningDue, 'Due now')}`}>
                          {profile.cervicalScreeningStatus || 'Due now'}
                        </span>
                        {formatScreeningSummary(profile.lastCervicalScreeningSummary, profile.nextCervicalScreeningDue) && (
                          <span className="text-xs text-base-content/50">
                            {formatScreeningSummary(profile.lastCervicalScreeningSummary, profile.nextCervicalScreeningDue)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Mammogram */}
                  {(profile.mammogramStatus || profile.lastMammogramSummary || profile.nextMammogramDue) && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px] pt-1">Mammogram:</span>
                      <div className="flex flex-col gap-1">
                        <span className={`badge badge-sm ${getScreeningBadgeStyle(profile.mammogramStatus, profile.nextMammogramDue, 'Not yet indicated')}`}>
                          {profile.mammogramStatus || 'Not yet indicated'}
                        </span>
                        {formatScreeningSummary(profile.lastMammogramSummary, profile.nextMammogramDue) && (
                          <span className="text-xs text-base-content/50">
                            {formatScreeningSummary(profile.lastMammogramSummary, profile.nextMammogramDue)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Colon */}
                  {profile.colonScreeningStatus && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px]">Colon:</span>
                      <div className="flex flex-col gap-1">
                        <span className="badge badge-neutral badge-sm">{profile.colonScreeningStatus}</span>
                        {profile.lastColonScreeningSummary && (
                          <span className="text-xs text-base-content/50">{profile.lastColonScreeningSummary}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* STI */}
                  {profile.stiScreeningStatus && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px]">STI:</span>
                      <span className="badge badge-neutral badge-sm">{profile.stiScreeningStatus}</span>
                    </div>
                  )}
                  {/* Vaccinations */}
                  {profile.vaccinationTags && profile.vaccinationTags.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-base-content/60 min-w-[100px] pt-1">Vaccinations:</span>
                      <div className="flex flex-wrap gap-2">
                        {profile.vaccinationTags.map((tag, index) => (
                          <span key={index} className="badge badge-neutral badge-sm">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Risk Factors */}
          {((profile.familyHistoryRiskTags && profile.familyHistoryRiskTags.length > 0) || 
            (profile.chronicGynConditionTags && profile.chronicGynConditionTags.length > 0)) && (
            <div className="card bg-base-100 border border-base-300 shadow-sm">
              <div className="card-body p-5">
                <h3 className="text-sm font-bold text-base-content/70 uppercase tracking-wide mb-4">Risk Factors</h3>
                <div className="space-y-2">
                  {profile.familyHistoryRiskTags && profile.familyHistoryRiskTags.map((tag, index) => (
                    <div key={index} className={`text-sm ${
                      isNegativeStatement(tag) 
                        ? 'text-base-content' 
                        : 'text-base-content'
                    }`}>
                      {tag}
                    </div>
                  ))}
                  {profile.chronicGynConditionTags && profile.chronicGynConditionTags.map((tag, index) => (
                    <span key={index} className="badge badge-error badge-outline badge-sm">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderVisitContext = (summary: PatientVisitSummary) => {
    if (!summary.visitContextWellWoman) return null;

    const context = summary.visitContextWellWoman;

    return (
      <div className="mt-6 space-y-5">
        {/* Primary Concern and Key Plan */}
        {(context.primaryConcernThisVisit || context.keyPlanSummary) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {context.primaryConcernThisVisit && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-success/10">
                      <Stethoscope className="w-4 h-4 text-success" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Primary Concern</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.primaryConcernThisVisit}</p>
                </div>
              </div>
            )}
            {context.keyPlanSummary && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-info/10">
                      <FileText className="w-4 h-4 text-info" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Key Plan</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.keyPlanSummary}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Abnormal Vitals Alert - Show first if present */}
        {context.hasAbnormalVitalsFlag && context.abnormalVitalsSummary && (
          <div className="alert alert-error shadow-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">Abnormal Vitals</div>
              <div className="text-sm">{context.abnormalVitalsSummary}</div>
            </div>
          </div>
        )}

        {/* Actions Completed */}
        {context.actionsCompletedThisVisit && context.actionsCompletedThisVisit.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Actions Completed</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.actionsCompletedThisVisit.map((action: string, index: number) => (
                <div key={index} className="badge badge-success badge-lg gap-2 px-3 py-2.5 border border-success/30">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{action}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow-Up Actions */}
        {context.followUpActionItems && context.followUpActionItems.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-warning" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Follow-Up Actions</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.followUpActionItems.map((item: string, index: number) => (
                <div key={index} className="badge badge-warning badge-lg gap-2 px-3 py-2.5 border border-warning/30">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Screening Updates */}
        {context.screeningUpdatesThisVisit && context.screeningUpdatesThisVisit.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-info" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Screening Updates</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.screeningUpdatesThisVisit.map((update: string, index: number) => (
                <div key={index} className="badge badge-info badge-lg gap-2 px-3 py-2.5 border border-info/30">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{update}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPrenatalVisitContext = (summary: PatientVisitSummary) => {
    if (!summary.visitContextPrenatal) return null;

    const context = summary.visitContextPrenatal;

    return (
      <div className="mt-6 space-y-5">
        {/* Primary Concern and Key Plan */}
        {(context.primaryConcernThisVisit || context.keyPlanSummary) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {context.primaryConcernThisVisit && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-success/10">
                      <Stethoscope className="w-4 h-4 text-success" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Primary Concern</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.primaryConcernThisVisit}</p>
                </div>
              </div>
            )}
            {context.keyPlanSummary && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-info/10">
                      <FileText className="w-4 h-4 text-info" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Key Plan</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.keyPlanSummary}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Red Flag Symptoms Alert - Show first if present */}
        {context.hasRedFlagSymptomsFlag && context.redFlagSummary && (
          <div className="alert alert-error shadow-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">Red Flag Symptoms</div>
              <div className="text-sm">{context.redFlagSummary}</div>
            </div>
          </div>
        )}

        {/* High Risk Pregnancy Alert */}
        {context.highRiskPregnancyFlag && context.highRiskPregnancySummary && (
          <div className="alert alert-warning shadow-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">High Risk Pregnancy</div>
              <div className="text-sm">{context.highRiskPregnancySummary}</div>
            </div>
          </div>
        )}

        {/* Actions Completed */}
        {context.actionsCompletedThisVisit && context.actionsCompletedThisVisit.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Actions Completed</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.actionsCompletedThisVisit.map((action: string, index: number) => (
                <div key={index} className="badge badge-success badge-lg gap-2 px-3 py-2.5 border border-success/30">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{action}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow-Up Actions */}
        {context.followUpActionItems && context.followUpActionItems.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-warning" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Follow-Up Actions</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.followUpActionItems.map((item: string, index: number) => (
                <div key={index} className="badge badge-warning badge-lg gap-2 px-3 py-2.5 border border-warning/30">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prenatal Screening Updates */}
        {context.prenatalScreeningUpdatesThisVisit && context.prenatalScreeningUpdatesThisVisit.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-info" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Screening Updates</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.prenatalScreeningUpdatesThisVisit.map((update: string, index: number) => (
                <div key={index} className="badge badge-info badge-lg gap-2 px-3 py-2.5 border border-info/30">
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{update}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInitialPrenatalVisitContext = (summary: PatientVisitSummary) => {
    if (!summary.visitContextInitialPrenatal) return null;

    const context = summary.visitContextInitialPrenatal;

    return (
      <div className="mt-6 space-y-5">
        {/* Primary Concern and Dating Status */}
        {(context.primaryConcernThisVisit || context.datingAndViabilityStatus || context.keyPlanSummary) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {context.primaryConcernThisVisit && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-success/10">
                      <Stethoscope className="w-4 h-4 text-success" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Primary Concern</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.primaryConcernThisVisit}</p>
                </div>
              </div>
            )}
            {context.datingAndViabilityStatus && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-primary/10">
                      <Activity className="w-4 h-4 text-primary" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Dating & Viability</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.datingAndViabilityStatus}</p>
                </div>
              </div>
            )}
            {context.keyPlanSummary && (
              <div className="card bg-base-200/50 border border-base-300 shadow-sm md:col-span-2">
                <div className="card-body p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-info/10">
                      <FileText className="w-4 h-4 text-info" />
                    </div>
                    <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Key Plan</h4>
                  </div>
                  <p className="text-sm text-base-content leading-relaxed">{context.keyPlanSummary}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Red Flag Symptoms Alert */}
        {context.hasRedFlagSymptomsFlag && context.redFlagSummary && (
          <div className="alert alert-error shadow-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">Red Flag Symptoms</div>
              <div className="text-sm">{context.redFlagSummary}</div>
            </div>
          </div>
        )}

        {/* High Risk Pregnancy Alert */}
        {context.highRiskPregnancyFlag && context.highRiskPregnancySummary && (
          <div className="alert alert-warning shadow-sm">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">High Risk Pregnancy</div>
              <div className="text-sm">{context.highRiskPregnancySummary}</div>
            </div>
          </div>
        )}

        {/* Pending Critical Results Alert */}
        {context.pendingCriticalResultsFlag && context.pendingCriticalResultsSummary && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/30">
            <Clock className="w-5 h-5 shrink-0 text-warning" />
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1 text-base-content">Pending Critical Results</div>
              <div className="text-sm text-base-content/80">{context.pendingCriticalResultsSummary}</div>
            </div>
          </div>
        )}

        {/* Actions Completed */}
        {context.actionsCompletedThisVisit && context.actionsCompletedThisVisit.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Actions Completed</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.actionsCompletedThisVisit.map((action: string, index: number) => (
                <div key={index} className="badge badge-success badge-lg gap-2 px-3 py-2.5 border border-success/30">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{action}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow-Up Actions */}
        {context.followUpActionItems && context.followUpActionItems.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-warning" />
              <h4 className="text-xs font-bold text-base-content/70 uppercase tracking-wider">Follow-Up Actions</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {context.followUpActionItems.map((item: string, index: number) => (
                <div key={index} className="badge badge-warning badge-lg gap-2 px-3 py-2.5 border border-warning/30">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-base-100">
      <div className="p-5 border-b border-base-300 bg-base-100">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="btn btn-ghost btn-sm btn-circle"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          {patient && (
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary-content flex items-center justify-center flex-shrink-0">
                <User size={20} />
              </div>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-semibold text-base-content truncate">{patient.name}</h2>
                  <p className="text-sm text-base-content/60 mt-0.5">Last seen {formatDateTime(patient.lastSeen)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setEditName(patient.name || ''); setShowEditName(true); }}
                  className="btn btn-sm btn-outline rounded-full px-4 h-9 min-h-9 border-base-300 flex-shrink-0"
                >
                  Edit Patient Name
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 p-6 overflow-visible bg-base-50">
        {isLoading ? (
          <div className="flex items-center justify-center h-64 text-base-content/70 gap-3">
            <span className="loading loading-spinner loading-lg" />
            Loading patient...
          </div>
        ) : error ? (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        ) : !patient ? (
          <div className="flex items-center justify-center h-64 text-base-content/70">
            Patient not found.
          </div>
        ) : (
          <div className="max-w-5xl mx-auto">
            {/* Women's Health Profile */}
            {renderWomensHealthProfile()}

            <div className="flex items-center gap-3 mb-6 mt-8">
              <HeartPulse size={22} className="text-primary-content" />
              <h3 className="text-2xl font-semibold text-base-content">Visit Summaries</h3>
            </div>
            {visitSummaries.length === 0 ? (
              <div className="p-8 text-center text-base-content/70 border border-dashed border-base-300 rounded-lg bg-base-100">
                <HeartPulse size={32} className="mx-auto mb-3 text-base-content/40" />
                <p className="text-base">No visit summaries have been generated yet.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {visitSummaries.map((summary: PatientVisitSummary) => (
                  <div 
                    key={`${summary.userRequestId}-${summary.visitDate}`} 
                    className={`card bg-base-100 border border-base-300 shadow-md ${onRequestSelect ? 'cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all duration-200' : ''}`}
                    onClick={() => onRequestSelect?.(summary.userRequestId)}
                  >
                    <div className="card-body p-6">
                      {/* Date Header */}
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-base-content">
                          {formatDateTime(summary.visitDate)}
                        </h3>
                      </div>

                      {/* Summary Text */}
                      {summary.summary && (
                        <p className="text-base text-base-content leading-relaxed mb-5">
                          {replacePatientNamePlaceholder(summary.summary)}
                        </p>
                      )}

                      {/* LMP Badge */}
                      {summary.lmpDateIso && (
                        <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
                          <Activity size={14} />
                          LMP: {formatDate(summary.lmpDateIso)}
                        </div>
                      )}

                      {/* Visit Context - Well-Woman specific */}
                      {summary.visitType === "Annual_Well_Woman_Visit" && renderVisitContext(summary)}
                      
                      {/* Visit Context - Prenatal specific (routine) */}
                      {summary.visitType === "Prenatal_Visit" && renderPrenatalVisitContext(summary)}
                      
                      {/* Visit Context - Initial Prenatal specific */}
                      {summary.visitType === "Initial_Prenatal_Visit" && renderInitialPrenatalVisitContext(summary)}

                      {/* Footer */}
                      <div className="mt-6 pt-4 border-t border-base-300">
                        <p className="text-xs text-base-content/50">
                          Captured {formatDateTime(summary.capturedAt ?? summary.visitDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Patient Name Modal */}
      {showEditName && patient && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-[1px] flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg shadow-xl border border-base-300 w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-base-300">
              <h3 className="text-lg font-semibold text-base-content">Edit Patient Name</h3>
              <button
                type="button"
                onClick={() => setShowEditName(false)}
                className="btn btn-ghost btn-sm btn-circle"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <label htmlFor="editPatientName" className="label mb-2">
                <span className="label-text font-medium text-base-content">Patient Name</span>
              </label>
              <input
                id="editPatientName"
                type="text"
                className="input input-bordered w-full"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editName.trim() && !savingName) {
                    (async () => {
                      try {
                        setSavingName(true);
                        const updated = await api.updatePatient(patient.id, editName.trim());
                        setPatient(updated);
                        window.dispatchEvent(new CustomEvent('patientsUpdated', { detail: { patientId: patient.id, name: editName.trim() } }));
                        setShowEditName(false);
                      } catch (e) {
                        console.error('Failed to update patient name', e);
                      } finally {
                        setSavingName(false);
                      }
                    })();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-base-300">
              <button
                type="button"
                onClick={() => setShowEditName(false)}
                className="btn btn-outline btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!editName.trim() || savingName}
                onClick={async () => {
                  try {
                    setSavingName(true);
                    const updated = await api.updatePatient(patient.id, editName.trim());
                    setPatient(updated);
                    window.dispatchEvent(new CustomEvent('patientsUpdated', { detail: { patientId: patient.id, name: editName.trim() } }));
                    setShowEditName(false);
                  } catch (e) {
                    console.error('Failed to update patient name', e);
                  } finally {
                    setSavingName(false);
                  }
                }}
                className="btn btn-primary btn-sm"
              >
                {savingName ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

